import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ILocation } from '@ess-front/shared';
import { SelfExploreMapService } from '@domain/self-explore-map.service';
import { take, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { ExperienceDetailsService } from '@domain/experience-details.service';
import { ExperienceContentType, ExperienceEndpoint } from '@shared/models/be-experience.model';
import { FEExperience } from '@shared/models/fe-experience.model';
import { WindowEventsService } from '@app/core/services/window-events/window-events.service';
import { environment } from '@environment';
import { TranslationDomainService } from '@domain/translation-domain.service';
import { marker } from '@jsverse/transloco-keys-manager/marker';

type TouchPosition = { x: number; y: number };

@Component({
  selector: 'ess-self-explore-modal',
  templateUrl: './self-explore-modal.component.html',
  styleUrls: ['./self-explore-modal.component.scss'],
  providers: [SelfExploreMapService],
  standalone: false,
})
export class SelfExploreModalComponent implements AfterViewInit, OnDestroy {
  relatedExperiences: ILocation[];
  @ViewChild('map') mapEl: ElementRef | null = null;
  @ViewChild('overlay') overlayEl: ElementRef | null = null;

  expandedExperience$: Observable<FEExperience> | null = null;
  selectedExperienceId: number | null = null;
  public readonly NUMBER_URL = environment.cloudinaryURL + 'v1654236851/experience/icons/markers/number-outline-';

  private destroy$ = new Subject<void>();
  private startTouchPosition: TouchPosition | null = null;
  private endTouchPosition: TouchPosition | null = null;
  private visibleCardIndex = 0;
  private readonly OVERLAY_INACTIVE_HEIGHT = 300;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { relatedExperiences: ILocation[] },
    private dialogRef: MatDialogRef<SelfExploreModalComponent>,
    private experienceService: ExperienceDetailsService,
    private selfExploreMapService: SelfExploreMapService,
    private translationDomainService: TranslationDomainService,
    private windowEventsService: WindowEventsService,
  ) {
    this.relatedExperiences = data.relatedExperiences;
    this.windowEventsService.isMobileResolution$.pipe(takeUntil(this.destroy$)).subscribe(isMobile => {
      if (!isMobile) {
        this.dialogRef.close();
      }
    });
  }

  async ngAfterViewInit() {
    await this.initializeMap();
  }

  public scrollExperienceIntoViewFactory(): (index: number) => void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const modalComponent = this; // Closure to keep context on self-explore-map.service
    return (index: number) => {
      const movement = modalComponent.visibleCardIndex - index;
      if (movement > 0) {
        modalComponent.swipeRight(movement, false);
      } else if (movement < 0) {
        modalComponent.swipeLeft(Math.abs(movement), false);
      }
    };
  }

  expandCard(experience: ILocation): void {
    if (experience.id === this.selectedExperienceId) return;
    this.selectedExperienceId = experience.id ?? null;
    const isBooking = experience.content_type === ExperienceContentType.BookingExperienceModule;
    const endpoint = this.convertContentTypeOnEndpoint(experience.content_type as ExperienceContentType);
    if (experience.slug) {
      this.expandedExperience$ = this.experienceService
        .getFEExperience$(isBooking, endpoint, { slug: experience.slug, id: null, hash: null }, true)
        .pipe(take(1));
    }
  }

  public onTouchStart(e: TouchEvent | MouseEvent) {
    // Care, Safari does NOT support TouchEvents
    const clientX = e instanceof MouseEvent ? e.clientX : e.changedTouches[0].clientX;
    const clientY = e instanceof MouseEvent ? e.clientY : e.changedTouches[0].clientY;
    this.startTouchPosition = { x: clientX, y: clientY };
  }

  public onTouchEnd(e: TouchEvent | MouseEvent, experience: ILocation) {
    if (this.startTouchPosition) {
      const clientX = e instanceof MouseEvent ? e.clientX : e.changedTouches[0].clientX;
      const clientY = e instanceof MouseEvent ? e.clientY : e.changedTouches[0].clientY;
      this.endTouchPosition = { x: clientX, y: clientY };
      const verticalMove = this.startTouchPosition.y - this.endTouchPosition.y;
      const horizontalMove = this.startTouchPosition.x - this.endTouchPosition.x;
      this.startTouchPosition = null;
      const isVertical = Math.abs(verticalMove) > Math.abs(horizontalMove);
      if (isVertical && Math.abs(verticalMove) > 20) {
        verticalMove >= 0 ? this.swipeUp() : this.swipeDown();
      } else if (!isVertical && Math.abs(horizontalMove) > 20) {
        horizontalMove >= 0 ? this.swipeLeft(1) : this.swipeRight(1);
      } else {
        this.expandCard(experience);
      }
    }
  }

  swipeLeft(movements: number, changeMarker = true) {
    if (this.visibleCardIndex + movements < this.relatedExperiences.length) {
      this.visibleCardIndex += movements;
      this.moveCard(this.visibleCardIndex);
      if (changeMarker) {
        this.selfExploreMapService.changeActiveMarker(this.visibleCardIndex);
      }
    }
  }

  swipeRight(movements: number, changeMarker = true) {
    if (this.visibleCardIndex - movements >= 0) {
      this.visibleCardIndex -= movements;
      this.moveCard(this.visibleCardIndex);
      if (changeMarker) {
        this.selfExploreMapService.changeActiveMarker(this.visibleCardIndex);
      }
    }
  }

  public closeCard() {
    this.selectedExperienceId = null;
  }

  swipeUp() {
    if (this.selectedExperienceId !== this.relatedExperiences[this.visibleCardIndex].id) {
      this.selectedExperienceId = this.relatedExperiences[this.visibleCardIndex].id ?? null;
    }
  }

  swipeDown() {
    if (this.selectedExperienceId === this.relatedExperiences[this.visibleCardIndex].id) {
      if (this.overlayEl?.nativeElement.children[0].scrollTop === 0) {
        this.selectedExperienceId = null;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private async initializeMap() {
    await this.selfExploreMapService.createMap(
      this.mapEl?.nativeElement,
      this.relatedExperiences,
      true,
      this.scrollExperienceIntoViewFactory(),
      true,
    );
    if (this.overlayEl) {
      await this.selfExploreMapService.offsetMapBottom(this.OVERLAY_INACTIVE_HEIGHT);
    }
  }

  private convertContentTypeOnEndpoint(contentType: ExperienceContentType) {
    switch (contentType) {
      case ExperienceContentType.Hotel:
        return ExperienceEndpoint.Hotel;
      case ExperienceContentType.Villa:
        return ExperienceEndpoint.Hotel;
      case ExperienceContentType.Experience:
        return ExperienceEndpoint.Experience;
      case ExperienceContentType.BookingExperienceModule:
        return ExperienceEndpoint.BookingExperience;
      case ExperienceContentType.Place:
        return ExperienceEndpoint.Place;
      default:
        throw new Error(
          this.translationDomainService.translate(marker('errors.unexpectedContentType'), { contentType }),
        );
    }
  }

  private moveCard(position: number) {
    if (this.overlayEl) {
      this.overlayEl.nativeElement.style = `transform: translateX(-${
        this.overlayEl.nativeElement.offsetWidth * 0.85 * position
      }px)`;
    }
  }
}
